import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSocialLinks } from '../utils/socialLinks';
import '../styles/contact.css';

const SubjectIcon = ({ id }) => {
  const props = {
    viewBox: '0 0 24 24', width: 18, height: 18,
    fill: 'none', stroke: 'currentColor', strokeWidth: 1.9,
    strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
  };
  switch (id) {
    case 'general':     return <svg {...props}><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/><circle cx="9" cy="11" r="1" fill="currentColor"/><circle cx="13" cy="11" r="1" fill="currentColor"/><circle cx="17" cy="11" r="1" fill="currentColor"/></svg>;
    case 'feedback':    return <svg {...props}><path d="M12 2 14.5 9 22 9.3 16 14 18 21 12 17 6 21 8 14 2 9.3 9.5 9z"/></svg>;
    case 'bug':         return <svg {...props}><rect x="8" y="6" width="8" height="13" rx="4"/><path d="M8 9 5 7M8 14 5 14M8 18 5 20M16 9l3-2M16 14h3M16 18l3 2M9 4l1 2M15 4l-1 2"/></svg>;
    case 'partnership': return <svg {...props}><path d="M3 12 9 6h4l3 3-2 2-2-2-3 3 4 4 7-7"/><path d="M3 12v6h6"/></svg>;
    case 'press':       return <svg {...props}><rect x="3" y="5" width="18" height="14" rx="2"/><line x1="7" y1="9" x2="13" y2="9"/><line x1="7" y1="13" x2="13" y2="13"/><line x1="7" y1="17" x2="11" y2="17"/><rect x="15" y="9" width="4" height="4"/></svg>;
    case 'other':       return <svg {...props}><circle cx="6" cy="12" r="1.4" fill="currentColor"/><circle cx="12" cy="12" r="1.4" fill="currentColor"/><circle cx="18" cy="12" r="1.4" fill="currentColor"/></svg>;
    default:            return null;
  }
};

const SUBJECTS = [
  { id: 'general',     label: 'General question',           hint: 'Ask us anything' },
  { id: 'feedback',    label: 'Feedback or suggestion',     hint: 'Help us improve' },
  { id: 'bug',         label: 'Report a bug',               hint: 'Something looks off' },
  { id: 'partnership', label: 'Partnership / school enquiry', hint: 'Work with us' },
  { id: 'press',       label: 'Press / media',              hint: 'Stories & interviews' },
  { id: 'other',       label: 'Other',                      hint: 'None of the above' },
];

function SubjectSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const current = SUBJECTS.find((s) => s.label === value) || SUBJECTS[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    setHighlight(SUBJECTS.findIndex((s) => s.label === value));
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, value]);

  const pick = (idx) => {
    onChange({ target: { value: SUBJECTS[idx].label } });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlight((h) => {
        const next = e.key === 'ArrowDown' ? h + 1 : h - 1;
        return (next + SUBJECTS.length) % SUBJECTS.length;
      });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) setOpen(true); else pick(highlight);
    } else if (e.key === 'Home') { e.preventDefault(); setHighlight(0); }
      else if (e.key === 'End')  { e.preventDefault(); setHighlight(SUBJECTS.length - 1); }
  };

  return (
    <div className="subject-select" ref={wrapRef}>
      <button
        type="button"
        className={`subject-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="subject-trigger-icon"><SubjectIcon id={current.id} /></span>
        <span className="subject-trigger-text">
          <span className="subject-trigger-label">{current.label}</span>
          <span className="subject-trigger-hint">{current.hint}</span>
        </span>
        <span className="subject-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && (
        <ul className="subject-menu" role="listbox" ref={listRef}>
          {SUBJECTS.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                role="option"
                aria-selected={s.label === value}
                className={`subject-option ${s.label === value ? 'is-selected' : ''} ${i === highlight ? 'is-highlight' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(i)}
              >
                <span className="subject-option-icon"><SubjectIcon id={s.id} /></span>
                <span className="subject-option-text">
                  <span className="subject-option-label">{s.label}</span>
                  <span className="subject-option-hint">{s.hint}</span>
                </span>
                {s.label === value && (
                  <span className="subject-option-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="5 12 10 17 19 7" />
                    </svg>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function readUserEmail() {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('adeeb-user') : null;
    if (!raw) return { name: '', email: '' };
    const u = JSON.parse(raw);
    return { name: u?.name || '', email: u?.email || '' };
  } catch {
    return { name: '', email: '' };
  }
}

export default function Contact() {
  const socials = useSocialLinks();
  const [form, setForm] = useState({ name: '', email: '', subject: SUBJECTS[0].label, message: '' });
  const [status, setStatus] = useState({ kind: 'idle' }); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const u = readUserEmail();
    if (u.name || u.email) setForm((f) => ({ ...f, name: u.name || f.name, email: u.email || f.email }));
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status.kind === 'sending') return;
    setStatus({ kind: 'sending' });
    setErrorMsg('');
    try {
      const headers = {};
      const token = localStorage.getItem('adeeb-token');
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.post('/api/contact', form, { headers, timeout: 10000 });
      setStatus({ kind: 'success' });
      setForm((f) => ({ ...f, subject: SUBJECTS[0].label, message: '' }));
    } catch (err) {
      setStatus({ kind: 'error' });
      setErrorMsg(err?.response?.data?.message || 'Could not send your message. Please try again.');
    }
  };

  const charsLeft = 4000 - form.message.length;

  return (
    <div className="contact-wrapper">
      <div className="contact-bg-orb contact-bg-orb-1" aria-hidden="true" />
      <div className="contact-bg-orb contact-bg-orb-2" aria-hidden="true" />

      <div className="contact-inner">
        <header className="contact-head">
          <div className="contact-eyebrow">
            <span className="contact-ornament">&#10040;</span>
            We&rsquo;d love to hear from you
            <span className="contact-ornament">&#10040;</span>
          </div>
          <h1 className="contact-title">Get in touch</h1>
          <p className="contact-sub">
            Questions, feedback, or just a hello — drop us a note and we&rsquo;ll get back to you within a couple of working days.
          </p>
        </header>

        <div className="contact-grid">
          <aside className="contact-info">
            <h2 className="contact-info-title">Reach us</h2>

            <a className="contact-info-row" href="mailto:support@deenlit.com">
              <span className="contact-info-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <polyline points="3 7 12 13 21 7" />
                </svg>
              </span>
              <div>
                <div className="contact-info-label">Email</div>
                <div className="contact-info-value">support@deenlit.com</div>
              </div>
            </a>

            <div className="contact-info-row">
              <span className="contact-info-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </span>
              <div>
                <div className="contact-info-label">Reply time</div>
                <div className="contact-info-value">Usually within 1&ndash;2 working days</div>
              </div>
            </div>

            <div className="contact-info-row">
              <span className="contact-info-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <div>
                <div className="contact-info-label">Studio</div>
                <div className="contact-info-value">Deenlit &middot; remote &amp; online</div>
              </div>
            </div>

            {socials.length > 0 && <div className="contact-socials" aria-label="Social links">
              {socials.map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`contact-social s-${s.id}`}
                  aria-label={s.label}
                  title={s.label}
                >
                  {s.id === 'instagram' && (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                    </svg>
                  )}
                  {s.id === 'facebook' && (
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
                    </svg>
                  )}
                  {s.id === 'x' && (
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                      <path d="M18.244 2H21l-6.51 7.44L22 22h-6.81l-4.74-6.18L4.93 22H2.17l6.97-7.97L2 2h6.94l4.29 5.67L18.244 2zm-2.39 18h1.91L7.24 4h-2L15.85 20z" />
                    </svg>
                  )}
                  {s.id === 'tiktok' && (
                    <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor">
                      <path d="M19.5 7.6a6.7 6.7 0 0 1-3.9-1.25v8.55a5.95 5.95 0 1 1-5.95-5.95c.32 0 .63.03.93.08v3.06a2.94 2.94 0 1 0 2.06 2.81V2h2.96a4 4 0 0 0 .27 1.45 4 4 0 0 0 1.04 1.5 4 4 0 0 0 2.59 1.05V7.6z" />
                    </svg>
                  )}
                  {s.id === 'youtube' && (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
                    </svg>
                  )}
                </a>
              ))}
            </div>}
          </aside>

          <form className="contact-form" onSubmit={submit} noValidate>
            <h2 className="contact-form-title">Send us a message</h2>

            <div className="contact-row contact-row-2">
              <label className="contact-field">
                <span className="contact-field-label">Your name</span>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={update('name')}
                  placeholder="e.g. Aisha"
                  autoComplete="name"
                />
              </label>
              <label className="contact-field">
                <span className="contact-field-label">Email</span>
                <input
                  type="email"
                  required
                  maxLength={200}
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>
            </div>

            <div className="contact-field">
              <span className="contact-field-label">Subject</span>
              <SubjectSelect value={form.subject} onChange={update('subject')} />
            </div>

            <label className="contact-field">
              <span className="contact-field-label">Message</span>
              <textarea
                required
                rows={6}
                maxLength={4000}
                value={form.message}
                onChange={update('message')}
                placeholder="Tell us what's on your mind…"
              />
              <span className={`contact-charcount ${charsLeft < 100 ? 'is-warn' : ''}`}>
                {form.message.length} / 4000
              </span>
            </label>

            {status.kind === 'error' && (
              <div className="contact-alert contact-alert-error" role="alert">{errorMsg}</div>
            )}
            {status.kind === 'success' && (
              <div className="contact-alert contact-alert-success" role="status">
                Thanks! Your message landed in our inbox. We&rsquo;ll be in touch soon.
              </div>
            )}

            <button
              type="submit"
              className="contact-submit"
              disabled={status.kind === 'sending'}
            >
              {status.kind === 'sending' ? (
                <>
                  <span className="contact-spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                <>
                  Send message
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="13 6 19 12 13 18" />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
